import { Alert, Box, Button, Skeleton, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconInfoCircle } from "@tabler/icons-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { TenantFeePolicyApi, TenantFeePolicyAssignmentApi } from "../../../../apis";
import type { FeePolicy } from "../../../../apis/tenantFeePolicy";
import { PolicyDefaultCard } from "../../../../components/billing/PolicyDefaultCard";
import { PolicyDraftListCard } from "../../../../components/billing/PolicyDraftListCard";
import { PolicyNamedListCard } from "../../../../components/billing/PolicyNamedListCard";
import { PolicyScheduledCard } from "../../../../components/billing/PolicyScheduledCard";

const FeePolicyPoliciesTab = () => {
  const navigate = useNavigate();
  const { data, error, isLoading, mutate } = useSWR("swr.my.fee-policies.all", () =>
    TenantFeePolicyApi.list({ page: 1, limit: 200 })
  );
  // Reach counters (DEFAULT + per-NAMED-policy customer counts) come from the
  // roster endpoint, which folds the full end-customer list against assignments.
  // limit:1 — we only read the aggregate counters, not the rows.
  const { data: customerCounts } = useSWR("swr.my.fee-policy-customers.counts", () =>
    TenantFeePolicyAssignmentApi.listCustomers({ page: 1, limit: 1 })
  );

  const all = useMemo<FeePolicy[]>(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawP = data as any;
    return Array.isArray(rawP) ? rawP : (rawP?.rows ?? []);
  }, [data]);

  const defaultActive = useMemo(
    () => all.find((p) => p.scope === "DEFAULT" && p.status === "ACTIVE") ?? null,
    [all]
  );
  // All pending SCHEDULED rows: brand-new scheduled policies AND future-dated amends
  // (which live as SCHEDULED rows linked to their source via sourcePolicyId).
  const scheduled = useMemo(
    () => all.filter((p) => p.status === "SCHEDULED"),
    [all]
  );
  const namedActive = useMemo(
    () => all.filter((p) => p.scope === "NAMED" && p.status === "ACTIVE"),
    [all]
  );
  const drafts = useMemo(() => all.filter((p) => p.status === "DRAFT"), [all]);

  const customerCountByPolicy = useMemo(
    () => customerCounts?.perPolicy ?? {},
    [customerCounts]
  );
  // Customers with no active NAMED assignment fall back to DEFAULT — the roster
  // endpoint reports this directly (gap A11).
  const defaultCustomerCount = customerCounts?.defaultCount;

  // The live source a scheduled row will replace, for the pending-change diff:
  // an amend points at its source; a brand-new scheduled policy compares against the
  // current active of the same scope/name.
  const sourceFor = (s: FeePolicy): FeePolicy | null => {
    if (s.sourcePolicyId) return all.find((p) => p.id === s.sourcePolicyId) ?? null;
    if (s.scope === "DEFAULT") return defaultActive;
    return all.find((p) => p.scope === "NAMED" && p.status === "ACTIVE" && p.name === s.name) ?? null;
  };

  if (isLoading) {
    return (
      <Box pt="md">
        <Stack gap="md">
          <Skeleton height={56} radius="md" />
          <Skeleton height={220} radius="md" />
          <Skeleton height={160} radius="md" />
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt="md">
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} title="Бодлогуудыг ачаалж чадсангүй">
          <Stack gap="sm" align="flex-start">
            <Text size="sm">Сүлжээний алдаа гарлаа. Дахин оролдоно уу.</Text>
            <Button size="xs" variant="default" onClick={() => mutate()}>Дахин ачаалах</Button>
          </Stack>
        </Alert>
      </Box>
    );
  }

  // Hierarchy (spec §2): Ноорог strip → DEFAULT hero → NAMED grid → SCHEDULED.
  return (
    <Box pt="md">
      <Stack gap="md">
        <Alert color="brand" icon={<IconInfoCircle size={16} />} variant="light">
          <Text span fw={700}>Та олон бодлого үүсгэж болно:</Text>{" "}нэг{" "}
          <Text span fw={700}>Үндсэн</Text> (бүх харилцагчдад автоматаар хэрэглэгдэх суурь) болон хэдэн ч{" "}
          <Text span fw={700}>Нэрлэсэн</Text> бодлого (тодорхой харилцагчдад оноох — жишээ нь "VIP", "Bronze").
        </Alert>

        <PolicyDraftListCard drafts={drafts} onCanceled={() => mutate()} />

        <PolicyDefaultCard policy={defaultActive} customerCount={defaultCustomerCount} />

        <PolicyNamedListCard
          policies={namedActive}
          customerCountByPolicy={customerCountByPolicy}
          countsLoaded={!!customerCounts}
        />

        {scheduled.map((s) => (
          <PolicyScheduledCard
            key={s.id}
            scheduled={s}
            active={sourceFor(s)}
            onCanceled={() => mutate()}
            onView={(p) => navigate(`/my-billing/fee-policy/${p.id}`)}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default FeePolicyPoliciesTab;
